/**
 * Iter XX.270 — "Build Your Gully" narrative (Phase 1).
 *
 * Factual accumulation sentence rendered inside Me → Memory tab
 * under the positioning eyebrow.
 *
 * Copy (approved):
 *   "Your Gully now knows N patterns, M focus areas, and K moments
 *    from your videos."
 *
 * Rules — hard invariants:
 *   1. Every category count maps 1:1 to a persisted collection the
 *      user could open right now. No fabricated numbers, no scores,
 *      no percentages, no completion metric.
 *   2. Category with a zero count is OMITTED from the sentence.
 *      Zero is never rendered as "0".
 *   3. Nothing renders unless at least ONE category has count >= 1.
 *   4. Grammar adapts naturally:
 *        1 item  → singular  ("1 pattern")
 *        >1 item → plural    ("3 patterns")
 *      Segments are joined with commas + Oxford "and":
 *        one seg    → "A."
 *        two segs   → "A and B."
 *        three segs → "A, B, and C."
 *
 * Data source:
 *   Reuses existing GET /api/web/knows payload already fetched by
 *   the parent KnowsTab in Me.jsx. Zero new endpoints.
 *
 * Analytics:
 *   Fires `gully_knows_line_seen` exactly once per session via
 *   activationFunnel.trackFunnel(). Gated upstream by the
 *   `gully_narrative_enabled` feature flag — the parent decides
 *   whether to mount this component at all.
 */
import React, { useEffect, useMemo } from "react";
import { trackFunnel } from "../lib/activationFunnel";

function pluralize(n, singular, plural) {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Join segments with commas and an Oxford "and":
 *   [A]        → "A"
 *   [A, B]     → "A and B"
 *   [A, B, C]  → "A, B, and C"
 */
function joinWithAnd(segments) {
  const parts = segments.filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  const head = parts.slice(0, -1).join(", ");
  return `${head}, and ${parts[parts.length - 1]}`;
}

/**
 * Compute the three factual counts from an /api/web/knows payload.
 *
 * · patterns      = distinct persisted `player_memory.patterns`.
 * · focus areas   = strengths + pressure_triggers (both persisted
 *                   in player_memory; the user is actively working
 *                   on / around them).
 * · video moments = successful video_analyses count (already
 *                   exposed as `stats.video_reviews`).
 */
export function computeGullyKnowsCounts(data) {
  if (!data || typeof data !== "object") {
    return { patterns: 0, focusAreas: 0, videoMoments: 0 };
  }
  const patterns = Array.isArray(data.patterns) ? data.patterns.length : 0;
  const strengths = Array.isArray(data.strengths) ? data.strengths.length : 0;
  const triggers = Array.isArray(data.pressure_triggers)
    ? data.pressure_triggers.length
    : 0;
  const videoMoments = Number(data?.stats?.video_reviews) || 0;
  return {
    patterns,
    focusAreas: strengths + triggers,
    videoMoments,
  };
}

/**
 * Build the final factual sentence (or empty string when nothing
 * to say). Exported for unit-testing so we can lock the grammar
 * rules without a DOM.
 */
export function buildGullyKnowsSentence(counts) {
  const { patterns = 0, focusAreas = 0, videoMoments = 0 } = counts || {};
  const segments = [];
  if (patterns > 0) segments.push(pluralize(patterns, "pattern", "patterns"));
  if (focusAreas > 0) {
    segments.push(pluralize(focusAreas, "focus area", "focus areas"));
  }
  if (videoMoments > 0) {
    segments.push(
      `${videoMoments} ${videoMoments === 1 ? "moment" : "moments"} from your ${videoMoments === 1 ? "video" : "videos"}`,
    );
  }
  if (segments.length === 0) return "";
  return `Your Gully now knows ${joinWithAnd(segments)} about your cricket.`;
}

export default function GullyKnowsLine({ data }) {
  const counts = useMemo(() => computeGullyKnowsCounts(data), [data]);
  const sentence = useMemo(() => buildGullyKnowsSentence(counts), [counts]);

  // Fire the impression event exactly once per session, but only
  // once we have a real non-empty sentence. `trackFunnel` is
  // already session-deduped so re-mounts are safe.
  useEffect(() => {
    if (!sentence) return;
    try {
      trackFunnel("gully_knows_line_seen", {
        patterns: counts.patterns,
        focus_areas: counts.focusAreas,
        video_moments: counts.videoMoments,
      });
    } catch { /* fire-and-forget */ }
  }, [sentence, counts.patterns, counts.focusAreas, counts.videoMoments]);

  if (!sentence) return null;

  return (
    <div
      data-testid="gully-knows-line"
      className="text-sm text-neutral-300 leading-relaxed mb-4 -mt-2"
    >
      {sentence}
    </div>
  );
}
